import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LanguageContext = createContext();

const translations = {
  en: {
    // Meta
    'meta.title': 'Luxury Sky Blue Quinceañera Dresses | Your Dream Dress Awaits',
    'meta.description': 'Discover the magic of blue quinceañera dresses. Shop our stunning luxury sky blue quinceañera dress with exquisite details, custom sizing, and free alterations. Perfect for your special day.',
    
    // Navigation
    'nav.home': 'Home',
    'nav.featured': 'Featured Dress',
    'nav.shades': 'Shades of Blue',
    'nav.faq': 'FAQ',
    
    // Hero
    'hero.subtitle': 'Your Dream Quinceañera Dress Awaits',
    'hero.title': 'The Magic of Blue',
    'hero.description': 'Your quinceañera is more than a party; it\'s a rite of passage, a celebration of culture, family, and the incredible young woman you are becoming.',
    'hero.cta': 'Discover Your Dream Dress',
    'hero.scroll': 'Scroll to explore',
    
    // Why Blue Section
    'why.subtitle': 'Why Choose Blue',
    'why.title': 'For Your Quinceañera?',
    'why.description': 'Blue is the color of serenity, depth, and majesty. It symbolizes trust, wisdom, and confidence—qualities that shine as you step into this new chapter of your life.',
    'why.point1.title': 'Timeless Elegance',
    'why.point1.desc': 'Unlike more traditional colors, a blue gown makes a statement of individuality and grace.',
    'why.point2.title': 'Versatile Beauty',
    'why.point2.desc': 'From soft sky-blue fairy tales to bold royal blue statements, this hue offers unparalleled versatility.',
    'why.point3.title': 'Symbolic Meaning',
    'why.point3.desc': 'Blue represents serenity, wisdom, and confidence—perfect for your transformation.',
    
    // Featured Product
    'featured.subtitle': 'Introducing The Showstopper',
    'featured.title': 'The Luxury Sky Blue Quinceañera Dress',
    'featured.description': 'For the young woman who wants to feel like royalty, our featured gown is designed to turn your vision into reality.',
    'featured.feature1.title': 'A Dreamy Silhouette',
    'featured.feature1.desc': 'A classic ball gown shape that gives you a princess-perfect profile, complete with a flowing, floor-length tulle skirt.',
    'featured.feature2.title': 'Exquisite Detail',
    'featured.feature2.desc': 'Intricate floral appliqués, delicate lace, shimmering crystals, and elegant embroidery.',
    'featured.feature3.title': 'A Flattering Neckline',
    'featured.feature3.desc': 'The off-shoulder design paired with a V-neck creates an elegant, romantic frame.',
    'featured.feature4.title': 'Thoughtful Design',
    'featured.feature4.desc': 'Built-in bra for support and comfort, so you can dance the night away with confidence.',
    'featured.customizable': 'Fully customizable in size and available in a spectrum of blues',
    'featured.cta': 'View This Stunning Dress',
    'featured.price': 'Starting from',
    
    // Gallery
    'gallery.subtitle': 'View Every Detail',
    'gallery.title': 'Dress Gallery',
    'gallery.main': 'Luxury Sky Blue Quinceañera Dress - Front View',
    'gallery.detail1': 'Bodice Detail with Lace Appliqués',
    'gallery.detail2': 'Corset Back Design',
    'gallery.detail3': 'Tulle Skirt Silhouette',
    'gallery.detail4': 'Floral Embellishment Detail',
    
    // Testimonials
    'testimonials.subtitle': 'Real Customers',
    'testimonials.title': 'Real Stories',
    'testimonials.description': 'See what our beautiful quinceañeras have to say about their dream dresses.',
    'testimonials.location1': 'Los Angeles, CA',
    'testimonials.text1': 'My dress arrived perfectly! The sky blue color was even more beautiful in person. I felt like a princess at my quinceañera. The custom sizing fit like a dream!',
    'testimonials.date1': 'Verified Purchase - December 2024',
    'testimonials.location2': 'Houston, TX',
    'testimonials.text2': 'TrendyKool was amazing to work with. They helped me customize the bodice and the result was stunning. All my guests couldn\'t stop complimenting my dress!',
    'testimonials.date2': 'Verified Purchase - November 2024',
    'testimonials.location3': 'Miami, FL',
    'testimonials.text3': 'The quality exceeded my expectations! The crystals sparkled beautifully under the lights. Worth every penny for my special day. Highly recommend!',
    'testimonials.date3': 'Verified Purchase - October 2024',
    'testimonials.rating': '4.9 out of 5',
    'testimonials.reviews': '500+ Happy Customers',
    
    // Size Chart
    'sizechart.button': 'View Size Chart',
    'sizechart.title': 'Size Chart & Measuring Guide',
    'sizechart.customNote': 'Free Custom Sizing Available!',
    'sizechart.customDesc': 'Don\'t see your exact size? Simply provide your measurements and we\'ll create a dress that fits you perfectly at no extra cost.',
    'sizechart.imperial': 'US Sizes (Inches)',
    'sizechart.metric': 'Metric Sizes (Centimeters)',
    'sizechart.size': 'Size',
    'sizechart.bust': 'Bust',
    'sizechart.waist': 'Waist',
    'sizechart.hips': 'Hips',
    'sizechart.height': 'Height',
    'sizechart.howTo': 'How to Measure',
    'sizechart.bustHow': 'Measure around the fullest part of your bust, keeping the tape level.',
    'sizechart.waistHow': 'Measure around your natural waistline, the narrowest part of your waist.',
    'sizechart.hipsHow': 'Measure around the fullest part of your hips, about 8 inches below your waist.',
    'sizechart.heightHow': 'Stand barefoot against a wall and measure from floor to the top of your head.',
    
    // Colors
    'colors.title': 'Choose Your Perfect Blue',
    'colors.subtitle': 'Free color customization available',
    'colors.selected': 'Selected',
    'colors.skyBlue': 'Sky Blue',
    'colors.powderBlue': 'Powder Blue',
    'colors.babyBlue': 'Baby Blue',
    'colors.royalBlue': 'Royal Blue',
    'colors.cornflowerBlue': 'Cornflower Blue',
    'colors.steelBlue': 'Steel Blue',
    'colors.navyBlue': 'Navy Blue',
    'colors.midnightBlue': 'Midnight Blue',
    
    // Shipping
    'shipping.production': 'Production Time',
    'shipping.productionDesc': '5-15 days standard, 3-5 days rush',
    'shipping.delivery': 'Worldwide Shipping',
    'shipping.deliveryDesc': 'Fast & secure delivery',
    'shipping.guarantee': 'Quality Guarantee',
    'shipping.guaranteeDesc': '100% satisfaction promise',
    'shipping.returns': 'Easy Returns',
    'shipping.returnsDesc': '30-day return policy',
    
    // Shades Section
    'shades.subtitle': 'Find Your Shade',
    'shades.title': 'Of Blue',
    'shades.description': 'Blue speaks in many tones, each with its own personality. Which one resonates with you?',
    'shades.sky.title': 'Sky Blue',
    'shades.sky.desc': 'Soft, dreamy, and ethereal. Perfect for a fairy-tale or garden-themed celebration.',
    'shades.royal.title': 'Royal Blue',
    'shades.royal.desc': 'Vibrant, bold, and regal. For the young woman who wants to command the room.',
    'shades.navy.title': 'Navy Blue',
    'shades.navy.desc': 'Sophisticated, timeless, and deeply elegant. Ideal for a formal, classic quinceañera.',
    'shades.twotone.title': 'Two-Toned Blue',
    'shades.twotone.desc': 'Modern, unique, and fashion-forward. A stunning choice for a personalized statement.',
    
    // About/Promise
    'about.subtitle': 'Crafted For You',
    'about.title': 'By Dreamers',
    'about.description': 'This exquisite gown is offered by TrendyKool, a store operated by a passionate group of young dreamers dedicated to making your vision come true.',
    'about.promise': 'Their Promise to You',
    'about.item1': 'Free Customization: Free color matching from a color card.',
    'about.item2': 'Perfect Fit: Provide your measurements for a dress made to fit flawlessly.',
    'about.item3': 'Open Communication: Changes to the original design are not a problem.',
    'about.item4': 'Support Every Step: From your first question to the final fitting.',
    
    // CTA Section
    'cta.title': 'Your Dream Dress Is a Click Away',
    'cta.description': 'Ready to see this stunning Sky Blue Quinceañera Dress up close and make it yours? Every intricate detail, customizable option, and secure ordering page is waiting for you.',
    'cta.button': 'Shop Now - View Luxury Sky Blue Dress',
    'cta.note': 'Free customization • Perfect fit guarantee • Ships worldwide',
    
    // FAQ
    'faq.subtitle': 'Questions?',
    'faq.title': 'We\'ve Got Answers',
    'faq.q1': 'How long does it take to make and ship my dress?',
    'faq.a1': 'Production typically takes 5-15 days, with expedited options of 3-5 days. The total delivery time will be shown at checkout.',
    'faq.q2': 'What if I need help with sizing?',
    'faq.a2': 'The product page includes a detailed size chart and measuring guide. Simply provide your bust, waist, hips, and height measurements for a custom-made fit.',
    'faq.q3': 'What if I have a problem with my order?',
    'faq.a3': 'The TrendyKool team is committed to your satisfaction. They encourage you to contact them directly with any concerns, and they will work with you to find a solution.',
    
    // Footer
    'footer.tagline': 'Your quinceañera is a once-in-a-lifetime moment. You deserve a dress that makes you feel as extraordinary as you are.',
    'footer.final': 'Find your perfect blue and begin your story.',
    'footer.copyright': '© 2025 Blue Quinceañera Dresses. All rights reserved.',
    'footer.powered': 'Powered by TrendyKool',
  },
  es: {
    // Meta
    'meta.title': 'Vestidos de Quinceañera Azul Cielo de Lujo | Tu Vestido Soñado Te Espera',
    'meta.description': 'Descubre la magia de los vestidos de quinceañera azules. Compra nuestro impresionante vestido de quinceañera azul cielo de lujo con detalles exquisitos, tallas personalizadas y alteraciones gratuitas. Perfecto para tu día especial.',
    
    // Navigation
    'nav.home': 'Inicio',
    'nav.featured': 'Vestido Destacado',
    'nav.shades': 'Tonos de Azul',
    'nav.faq': 'Preguntas',
    
    // Hero
    'hero.subtitle': 'Tu Vestido de Quinceañera Soñado Te Espera',
    'hero.title': 'La Magia del Azul',
    'hero.description': 'Tu quinceañera es más que una fiesta; es un rito de paso, una celebración de la cultura, la familia y la increíble mujer joven en la que te estás convirtiendo.',
    'hero.cta': 'Descubre Tu Vestido Soñado',
    'hero.scroll': 'Desliza para explorar',
    
    // Why Blue Section
    'why.subtitle': 'Por Qué Elegir Azul',
    'why.title': 'Para Tu Quinceañera?',
    'why.description': 'El azul es el color de la serenidad, la profundidad y la majestuosidad. Simboliza la confianza, la sabiduría y la seguridad—cualidades que brillan mientras das un paso hacia este nuevo capítulo de tu vida.',
    'why.point1.title': 'Elegancia Atemporal',
    'why.point1.desc': 'A diferencia de colores más tradicionales, un vestido azul hace una declaración de individualidad y gracia.',
    'why.point2.title': 'Belleza Versátil',
    'why.point2.desc': 'Desde cuentos de hadas azul cielo hasta declaraciones azul real audaces, este tono ofrece versatilidad sin igual.',
    'why.point3.title': 'Significado Simbólico',
    'why.point3.desc': 'El azul representa serenidad, sabiduría y confianza—perfecto para tu transformación.',
    
    // Featured Product
    'featured.subtitle': 'Presentamos La Estrella',
    'featured.title': 'El Vestido de Quinceañera Azul Cielo de Lujo',
    'featured.description': 'Para la joven que quiere sentirse como la realeza, nuestro vestido destacado está diseñado para convertir tu visión en realidad.',
    'featured.feature1.title': 'Una Silueta Soñada',
    'featured.feature1.desc': 'Una forma clásica de vestido de gala que te da un perfil perfecto de princesa, con una falda de tul hasta el suelo.',
    'featured.feature2.title': 'Detalles Exquisitos',
    'featured.feature2.desc': 'Aplicaciones florales intrincadas, encaje delicado, cristales brillantes y bordados elegantes.',
    'featured.feature3.title': 'Un Escote Favorecedor',
    'featured.feature3.desc': 'El diseño de hombros descubiertos combinado con un escote en V crea un marco elegante y romántico.',
    'featured.feature4.title': 'Diseño Pensado',
    'featured.feature4.desc': 'Sujetador incorporado para soporte y comodidad, para que puedas bailar toda la noche con confianza.',
    'featured.customizable': 'Totalmente personalizable en talla y disponible en un espectro de azules',
    'featured.cta': 'Ver Este Vestido Impresionante',
    'featured.price': 'Desde',
    
    // Gallery
    'gallery.subtitle': 'Ver Cada Detalle',
    'gallery.title': 'Galería del Vestido',
    'gallery.main': 'Vestido de Quinceañera Azul Cielo de Lujo - Vista Frontal',
    'gallery.detail1': 'Detalle del Corpiño con Aplicaciones de Encaje',
    'gallery.detail2': 'Diseño de Espalda con Corsé',
    'gallery.detail3': 'Silueta de Falda de Tul',
    'gallery.detail4': 'Detalle de Adornos Florales',
    
    // Testimonials
    'testimonials.subtitle': 'Clientes Reales',
    'testimonials.title': 'Historias Reales',
    'testimonials.description': 'Mira lo que nuestras hermosas quinceañeras dicen sobre sus vestidos soñados.',
    'testimonials.location1': 'Los Ángeles, CA',
    'testimonials.text1': '¡Mi vestido llegó perfecto! El color azul cielo era aún más hermoso en persona. Me sentí como una princesa en mi quinceañera. ¡El tamaño personalizado me quedó como un sueño!',
    'testimonials.date1': 'Compra Verificada - Diciembre 2024',
    'testimonials.location2': 'Houston, TX',
    'testimonials.text2': 'TrendyKool fue increíble para trabajar. Me ayudaron a personalizar el corpiño y el resultado fue impresionante. ¡Todos mis invitados no podían dejar de elogiar mi vestido!',
    'testimonials.date2': 'Compra Verificada - Noviembre 2024',
    'testimonials.location3': 'Miami, FL',
    'testimonials.text3': '¡La calidad superó mis expectativas! Los cristales brillaban hermosamente bajo las luces. Vale cada centavo por mi día especial. ¡Altamente recomendado!',
    'testimonials.date3': 'Compra Verificada - Octubre 2024',
    'testimonials.rating': '4.9 de 5',
    'testimonials.reviews': '500+ Clientes Felices',
    
    // Size Chart
    'sizechart.button': 'Ver Tabla de Tallas',
    'sizechart.title': 'Tabla de Tallas y Guía de Medición',
    'sizechart.customNote': '¡Tallas Personalizadas Gratis!',
    'sizechart.customDesc': '¿No ves tu talla exacta? Simplemente proporciona tus medidas y crearemos un vestido que te quede perfectamente sin costo adicional.',
    'sizechart.imperial': 'Tallas US (Pulgadas)',
    'sizechart.metric': 'Tallas Métricas (Centímetros)',
    'sizechart.size': 'Talla',
    'sizechart.bust': 'Busto',
    'sizechart.waist': 'Cintura',
    'sizechart.hips': 'Caderas',
    'sizechart.height': 'Altura',
    'sizechart.howTo': 'Cómo Medir',
    'sizechart.bustHow': 'Mide alrededor de la parte más llena de tu busto, manteniendo la cinta nivelada.',
    'sizechart.waistHow': 'Mide alrededor de tu cintura natural, la parte más estrecha de tu cintura.',
    'sizechart.hipsHow': 'Mide alrededor de la parte más llena de tus caderas, aproximadamente 20 cm debajo de tu cintura.',
    'sizechart.heightHow': 'Párate descalza contra una pared y mide desde el piso hasta la parte superior de tu cabeza.',
    
    // Colors
    'colors.title': 'Elige Tu Azul Perfecto',
    'colors.subtitle': 'Personalización de color gratuita disponible',
    'colors.selected': 'Seleccionado',
    'colors.skyBlue': 'Azul Cielo',
    'colors.powderBlue': 'Azul Polvo',
    'colors.babyBlue': 'Azul Bebé',
    'colors.royalBlue': 'Azul Real',
    'colors.cornflowerBlue': 'Azul Aciano',
    'colors.steelBlue': 'Azul Acero',
    'colors.navyBlue': 'Azul Marino',
    'colors.midnightBlue': 'Azul Medianoche',
    
    // Shipping
    'shipping.production': 'Tiempo de Producción',
    'shipping.productionDesc': '5-15 días estándar, 3-5 días urgente',
    'shipping.delivery': 'Envío Mundial',
    'shipping.deliveryDesc': 'Entrega rápida y segura',
    'shipping.guarantee': 'Garantía de Calidad',
    'shipping.guaranteeDesc': '100% promesa de satisfacción',
    'shipping.returns': 'Devoluciones Fáciles',
    'shipping.returnsDesc': 'Política de devolución de 30 días',
    
    // Shades Section
    'shades.subtitle': 'Encuentra Tu Tono',
    'shades.title': 'De Azul',
    'shades.description': 'El azul habla en muchos tonos, cada uno con su propia personalidad. ¿Cuál resuena contigo?',
    'shades.sky.title': 'Azul Cielo',
    'shades.sky.desc': 'Suave, soñador y etéreo. Perfecto para una celebración de cuento de hadas o temática de jardín.',
    'shades.royal.title': 'Azul Real',
    'shades.royal.desc': 'Vibrante, audaz y regio. Para la joven que quiere dominar la habitación.',
    'shades.navy.title': 'Azul Marino',
    'shades.navy.desc': 'Sofisticado, atemporal y profundamente elegante. Ideal para una quinceañera formal y clásica.',
    'shades.twotone.title': 'Azul Bicolor',
    'shades.twotone.desc': 'Moderno, único y vanguardista. Una opción impresionante para una declaración personalizada.',
    
    // About/Promise
    'about.subtitle': 'Creado Para Ti',
    'about.title': 'Por Soñadores',
    'about.description': 'Este vestido exquisito es ofrecido por TrendyKool, una tienda operada por un grupo apasionado de jóvenes soñadores dedicados a hacer realidad tu visión.',
    'about.promise': 'Su Promesa Para Ti',
    'about.item1': 'Personalización Gratuita: Coincidencia de color gratuita desde una carta de colores.',
    'about.item2': 'Ajuste Perfecto: Proporciona tus medidas para un vestido hecho a tu medida.',
    'about.item3': 'Comunicación Abierta: Los cambios al diseño original no son un problema.',
    'about.item4': 'Apoyo en Cada Paso: Desde tu primera pregunta hasta la prueba final.',
    
    // CTA Section
    'cta.title': 'Tu Vestido Soñado Está a Un Clic',
    'cta.description': '¿Lista para ver este impresionante Vestido de Quinceañera Azul Cielo de cerca y hacerlo tuyo? Cada detalle intrincado, opción personalizable y página de pedido segura te está esperando.',
    'cta.button': 'Comprar Ahora - Ver Vestido Azul Cielo de Lujo',
    'cta.note': 'Personalización gratuita • Garantía de ajuste perfecto • Envío mundial',
    
    // FAQ
    'faq.subtitle': '¿Preguntas?',
    'faq.title': 'Tenemos Respuestas',
    'faq.q1': '¿Cuánto tiempo tarda en hacerse y enviarse mi vestido?',
    'faq.a1': 'La producción típicamente toma 5-15 días, con opciones aceleradas de 3-5 días. El tiempo total de entrega se mostrará al finalizar la compra.',
    'faq.q2': '¿Qué pasa si necesito ayuda con las tallas?',
    'faq.a2': 'La página del producto incluye una tabla de tallas detallada y una guía de medición. Simplemente proporciona tus medidas de busto, cintura, caderas y altura para un ajuste personalizado.',
    'faq.q3': '¿Qué pasa si tengo un problema con mi pedido?',
    'faq.a3': 'El equipo de TrendyKool está comprometido con tu satisfacción. Te animan a contactarlos directamente con cualquier inquietud, y trabajarán contigo para encontrar una solución.',
    
    // Footer
    'footer.tagline': 'Tu quinceañera es un momento único en la vida. Mereces un vestido que te haga sentir tan extraordinaria como eres.',
    'footer.final': 'Encuentra tu azul perfecto y comienza tu historia.',
    'footer.copyright': '© 2025 Vestidos de Quinceañera Azul. Todos los derechos reservados.',
    'footer.powered': 'Impulsado por TrendyKool',
  }
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get initial language from pathname only (no navigation during initialization)
  const getInitialLanguage = () => {
    const pathname = location.pathname;
    if (pathname === '/mx') {
      return 'es';
    }
    // Default to English for '/' or any other path
    return 'en';
  };

  const [language, setLanguageState] = useState(getInitialLanguage);

  // Update URL and localStorage when language changes
  const setLanguage = (lang) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    
    // Navigate to appropriate path
    if (lang === 'es') {
      navigate('/mx', { replace: true });
    } else {
      navigate('/', { replace: true });
    }
    
    // Update document lang attribute
    document.documentElement.lang = lang;
  };

  useEffect(() => {
    // Update language based on current pathname
    const pathname = location.pathname;
    if (pathname === '/mx') {
      if (language !== 'es') {
        setLanguageState('es');
        localStorage.setItem('language', 'es');
      }
    } else if (pathname === '/') {
      if (language !== 'en') {
        setLanguageState('en');
        localStorage.setItem('language', 'en');
      }
    } else {
      // If pathname is neither '/' nor '/mx', redirect based on stored preference or browser language
      const storedLang = localStorage.getItem('language');
      const browserLang = navigator.language.toLowerCase();
      
      if (storedLang === 'es' || (browserLang.startsWith('es') && storedLang !== 'en')) {
        navigate('/mx', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
    
    // Update document lang attribute on mount and language change
    document.documentElement.lang = language;
  }, [location.pathname, language, navigate]);

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
